import React from 'react';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import useCheckIfEmailBlacklisted from '../api/useCheckIfEmailBlacklisted';
import SuppressionEmailForm from './SuppressionEmailForm';
import { SuppressionEmailParams } from './schema';
import P from '@hero/ui-kit/typography/P';
import Button from '@hero/ui-kit/inputs/Button';
import useRemoveEmailFromBlacklist from '../api/useRemoveEmailFromBlacklist';
import Modal, { Props as ModalProps } from '@hero/ui-kit/components/Modal';
import QueryInlineError from '../../../components/QueryInlineError';
import useAddEmailToSuppressionList from '../api/useAddEmailToSuppressionList';

type SuppressionEmailProps = {} & Pick<ModalProps, 'externalControls'>;

type InitialState = {
    isBlacklisted: boolean;
    email?: string;
};

const initialState: InitialState = {
    isBlacklisted: false,
    email: undefined
};

const SuppressionEmail: React.FC<SuppressionEmailProps> = ({ externalControls }) => {
    const [, setExternalState] = externalControls ?? [];
    const {
        mutateAsync: checkEmail,
        isSuccess: isCheckSuccess,
        isError: isCheckError,
        isPending: isCheckLoading,
        error: checkError,
        reset: checkReset
    } = useCheckIfEmailBlacklisted();
    const {
        mutateAsync: removeEmail,
        isSuccess: isRemoveSuccess,
        isError: isRemoveError,
        isPending: isRemoveLoading,
        error: removeError,
        reset: removeReset
    } = useRemoveEmailFromBlacklist();

    const {
        mutateAsync: addEmail,
        isSuccess: isAddSuccess,
        isError: isAddError,
        isPending: isAddLoading,
        error: addError,
        reset: addReset
    } = useAddEmailToSuppressionList();

    const handleClose = () => {
        setState(initialState);
        setExternalState && setExternalState(false);
        removeReset();
        checkReset();
        addReset();
    };

    const [state, setState] = React.useState<InitialState>(initialState);

    const handleCheck = (attributes: SuppressionEmailParams) => {
        setState(initialState);
        checkEmail(attributes)
            .then((response) => {
                setState({ isBlacklisted: response.data.suspended, email: response.data.email });
            })
            .catch(() => null);
    };

    const handleRemoveEmailFromBlacklist = () => {
        state.isBlacklisted
            ? state.email &&
              removeEmail({ email: state.email })
                  .then((response) => {
                      response.data.removed &&
                          setState({
                              isBlacklisted: false,
                              email: response.data.email
                          });
                  })
                  .catch(() => null)
            : state.email &&
              addEmail({ email: state.email, reason: 'COMPLAINT' })
                  .then((response) => {
                      response.status === 200 &&
                          setState({
                              isBlacklisted: true,
                              email: response.data.email
                          });
                  })
                  .catch(() => null);
    };

    const stateProps = React.useMemo(() => {
        let description = '';

        if (isCheckSuccess) {
            description = `Email: ${state.email} ${
                state.isBlacklisted ? 'is in' : 'is not in'
            } suppression list`;
        }
        if (isRemoveSuccess) {
            description = `Email: ${state.email} is successfully removed from suppression list.`;
        }

        if (isAddSuccess) {
            description = `Email: ${state.email} is successfully added to suppression list.`;
        }

        return {
            isSuccess: isCheckSuccess || isRemoveSuccess || isAddSuccess,
            description,
            btnLabel: state.isBlacklisted
                ? 'Remove Email from Suppression List'
                : 'Add Email to Suppression List'
        };
    }, [isCheckSuccess, isRemoveSuccess, state, isAddSuccess]);

    const getModalType = () => {
        if (isRemoveSuccess || isAddSuccess) {
            return 'success';
        }

        if (state.isBlacklisted) {
            return 'warn';
        }
        return 'neutral';
    };

    return (
        <Modal
            type={getModalType()}
            onClose={handleClose}
            isCancelable
            externalControls={externalControls}
        >
            <H role="h4">Suppression Email</H>
            {stateProps.isSuccess ? (
                <Section
                    noDefaultPadding
                    styles={{
                        overflowWrap: 'anywhere',
                        wordBreak: 'normal',
                        whiteSpace: 'pre-wrap'
                    }}
                >
                    <P strong size="large" centered>
                        {stateProps.description}
                    </P>
                    {isRemoveSuccess || isAddSuccess ? (
                        <Button width="full" onClick={handleClose}>
                            Ok
                        </Button>
                    ) : (
                        <Button
                            width="full"
                            onClick={handleRemoveEmailFromBlacklist}
                            disabled={isRemoveLoading || isAddLoading}
                        >
                            {stateProps.btnLabel}
                        </Button>
                    )}
                </Section>
            ) : (
                <SuppressionEmailForm onSubmit={handleCheck} isLoading={isCheckLoading} />
            )}
            {isRemoveError || isCheckError || isAddError ? (
                <QueryInlineError centered error={removeError || checkError || addError} />
            ) : null}
        </Modal>
    );
};

export default SuppressionEmail;
